import { cspInfractions } from './infractions/csp';
import { dkimInfractions } from './infractions/dkim';
import { dmarcInfractions } from './infractions/dmarc';
import { spfInfractions } from './infractions/spf';
import { hstsInfractions } from './infractions/hsts';
import { httpInfractions } from './infractions/http';
import { tlsInfractions } from './infractions/tls';
import { dnssecInfractions } from './infractions/dnssec';
import infoFindingsI18n from './info-findings/i18n';
import { portInfractions as remoteAccessPortInfractions } from './infractions/remoteAccessPorts';
import { portInfractions as webServicePortInfractions } from './infractions/webServicePorts';
import { portInfractions as infrastructurePortInfractions } from './infractions/infrastructurePorts';
import { portInfractions as emailPortInfractions } from './infractions/emailPorts';
import { portInfractions as fileSharingPortInfractions } from './infractions/fileSharingPorts';
import { portInfractions as databasePortInfractions } from './infractions/databasePorts';
import { portServiceVulnerableI18n } from './infractions/portServiceVulnerableI18n';

export default {
  en: {
    menu: {
      scans: 'Scan categories',
      assets: 'Assets',
      findings: 'Findings',
    },
    categories: {
      dnsSecurity: 'DNS security',
      emailSecurity: 'Email security',
      webSecurity: 'Web security',
      portSecurity: 'Port security',
    },
    categoryDescription: {
      dnsSecurity:
        'These controls ensure your DNS infrastructure is operating efficiently and reliably.',
      emailSecurity: 'These controls verify email legitimacy and build sending reputation.',
      webSecurity:
        'These configurations ensure secure content delivery and prevent malicious code.',
      portSecurity: `Port security identifies vulnerabilities by analysing your system's network ports, focussing on commonly exploited ports.`,
    },
    supplierMonitoring: {
      title: 'External monitoring',
      beta: 'Beta version: this is an early release and features may change based on feedback.',
      assetStatus: {
        ACTIVE: 'Active',
        DISABLED: 'Inactive',
        label: 'Status',
      },
      scanSeverity: {
        CRITICAL: 'Critical',
        HIGH: 'High',
        MEDIUM: 'Medium',
        LOW: 'Low',
        INFO: 'Informational',
        label: 'Severity',
      },
      activeAssets: 'Monitoring',
      activeAssetsDescription: 'Security monitoring is running regularly for these assets.',
      disabledAssets: 'Inactive',
      disabledAssetsDescription:
        'Security monitoring and asset discovery is paused for these assets.',
      createdAt: 'Added',
      createdBy: 'Added by',
      lastModifiedAt: 'Updated at',
      lastModifiedBy: 'Updated by',
      editAssetButton: 'Edit asset',
      addAssetButton: 'Add asset',
      addAssetModalTitle: 'Add asset',
      editAssetModalTitle: 'Edit asset',
      assetValue: 'Asset',
      assetValuePlaceholder: 'example.com',
      invalidDomainOrSubdomain: 'Please enter a valid domain or subdomain',
      assetValueRequired: 'Enter a domain or subdomain',
      assetDescription: 'Description of asset',
      assetDescriptionPlaceholder: 'Production website',
      assetDescriptionHelpText: 'This will be visible to your clients',
      monitoring: 'Monitoring',
      verified: 'Verified',
      verifiedYes: 'Yes',
      verifiedNo: 'No',
      saveAssetButton: 'Save asset',
      deleteAsset: 'Delete asset',
      deleteAssetSuccess: 'Asset deleted successfully',
      deleteAssetError: 'Error deleting asset',
      editAssetError: 'Error saving asset',
      addAssetError: 'Error adding asset',
      addAssetAlreadyExists: 'Asset already exists',
      addAssetSuccess: 'Asset added successfully',
      saveAssetSuccess: 'Asset saved successfully',
      rescanAssetError: 'Error triggering asset re-scan',
      assetCreatedBySystem: 'System',
      assetCreatedByDiscovery: 'Discovery',
      cancelButton: 'Cancel',
      emptyTitle: "You haven't added any assets yet",
      emptySubtitle: 'Add your first asset so we can display ',
      emptyCTA: 'Add an asset',
      systemStatusUpdateNotAllowed: "Your organisation's website cannot be set to inactive",
      registeredWebsiteSupplier: 'Registered website',
      registeredWebsite: 'Registered website',
      registeredWebsiteInOrganisationSettings:
        'This asset address can be edited in your {settings}',
      organisationSettings: 'organisation settings',
      allowances: {
        request: 'Increase asset limit',
        banner: 'You are near your asset limit. Request an increase to avoid reaching it. {n}/{m}',
        bannerFull: 'You are at your asset limit. Request an increase to raise the limit. {n}/{m}',
      },
      assetCategory: 'Category',
      assetCategories: {
        UNSPECIFIED: '-',
        API: 'API',
        APP: 'App',
        MARKETING: 'Marketing',
        SUPPORT: 'Support',
        DEVELOPMENT: 'Development',
        THIRD_PARTY: 'Third Party',
      },
    },
    assets: {
      discoveredTitle: 'Discovered',
      assetsTitle: 'Assets',
      createdAt: 'Added at',
      discoveredDescription:
        'Verify newly discovered assets so your clients can see your external security posture from external monitoring scans.',
      verifyAll: 'Verify all ({count})',
      verifySelected: 'Verify selected ({count})',
      verifying: 'Verifying...',
      columnAsset: 'Asset',
      columnCategory: 'Category',
      columnMonitored: 'Monitored',
      columnDiscoveredAt: 'Discovered at',
      columnActions: 'Actions',
      columnVerified: 'Verified',
      columnAddedAt: 'Added at',
      columnDescription: 'Description',
      scannedYes: 'Yes',
      scannedNoVerification: 'No',
      rejectAsset: 'Reject asset',
      verifyAsset: 'Verify asset',
      verifySuccess: 'Assets verified successfully',
      verifyError: 'Failed to verify assets',
      rejectSuccess: 'Assets rejected successfully',
      rejectError: 'Failed to reject assets',
      rejectModal: {
        title: 'Are you sure you want to delete this asset?',
        reasonQuestion: 'Select why you want to delete this asset',
        reasonPlaceholder: 'Choose reason (or type in your own)',
        cancel: 'Cancel',
        rejectButton: 'Remove asset',
        error: 'Failed to reject asset. Please try again.',
        bullets: {
          title: 'Removing this asset will:',
          removeFromExternalMonitor: 'Remove the asset from the external monitor feature',
          noMoreScanFindings: 'Result in no more scan findings for this asset',
        },
        reasons: {
          noLongerUsed: "My organisation doesn't have this asset anymore",
          notUsedForAssessment:
            "My organisation doesn't want this asset to be used for the security assessment",
          doesNotOwn: "My organisation doesn't own this asset",
          preferNotToSay: 'I prefer not to say',
        },
      },
      verified: 'Verified',
      verifiedBy: 'Verified by',
      verifiedNo: 'No',
      verifiedInfo: 'Shows if the supplier has verified ownership for this asset',
      verifiedInfoSupplier: `Assets that are labelled 'no' have not been manually verified`,
      monitoredInfoSupplier: `Assets marked as yes are continuously scanned for potential security risks`,
      isThisYourAsset: 'Is this asset part of your organisation?',
      showFindings: 'Show findings',
      openRisk: 'Open Risk',
      discoveryStatusTooltip: {
        client: {
          unverified: 'The supplier has not verified they own this asset',
          verified: 'The supplier has verified they own this asset',
        },
        supplier: {
          unverified: 'This asset has not been verified by your organisation',
          verified: 'This asset has been verified by your organisation',
        },
      },
      yourAssetIsVerified: 'Your asset is now verified',
      emptyDiscoveredTitle: 'No new assets have been discovered',
      emptyDiscoveredSubtitle: 'Any newly discovered assets will be listed here',
    },
    supplierMonitoringScans: {
      emptyTitle: "You don't have any scans yet",
      emptySubtitle: 'You will need to add an asset before we can display any scans',
      emptyCTA: 'Add an asset',
      scanningTitle: 'We are scanning your asset',
      scanningSubtitle: 'Come back in a few minutes to see the results',
      feedbackTitle: 'Give us your feedback',
      feedbackText:
        "We are still building out this feature and would love to hear how you're finding it.",
      infractionType: 'issue',
      infoFindingType: 'info',
      feedbackCTA: 'Send us feedback',
      exportFindingsTitle: 'Latest-Findings',
      exportTitle: 'Scan-Results',
      exportCSVButton: 'Export as CSV',
      exportHeaderAsset: 'Asset',
      exportHeaderScannedAt: 'Scanned at',
      exportHeaderScanType: 'Scan type',
      exportHeaderContextNote: 'Context note',
      exportHeaderPrivateNote: 'Private note',
      exportHeaderScanOutput: 'Scan output',
      exportHeaderFindingType: 'Finding type',
      exportHeaderFindingName: 'Finding name',
      exportHeaderFindingExplanation: 'Finding explanation',
      exportHeaderFindingSeverity: 'Finding severity',
      exportHeaderFindingSeverityLevel: 'Finding severity level',
      exportHeaderFindingContext: 'Finding context',
    },
    unclaimedSupplierMonitoringScans: {
      emptyTitle: 'This supplier has no assets to be monitored',
      emptySubtitle:
        'Results will be available when the supplier claims this profile and adds assets',
      scanningTitle: "This supplier's assets are currently being scanned",
      scanningSubtitle: 'The results will be available in a few minutes',
    },
    supplierTab: {
      emptyTitle: 'This supplier has not set up any assets to be monitored',
      emptySubtitle: 'Contact the supplier if you want them to share an asset',
      emptyCTA: 'Start a discussion',
      assetsTitle: 'Assets',
      assetsSubtitle:
        "The domains and hosts we scan to assess this supplier's external security position.",
      scansSubtitle:
        'The checks our external scans run, grouped by category, with any issues found for this supplier.',
      moreDomainsTitle: 'Not seeing an asset you use?',
      moreDomainsSubtitle:
        'Reach out to this supplier if you believe they should add an asset your organisation uses',
      moreDomainsCTA: 'Start a discussion',
    },
    associatedAsset: {
      unknown: 'Unknown asset',
      deleted: 'This asset has been deleted',
    },
    scanResult: {
      scanDescriptionTitle: 'What does this scan type check for?',
      scanLearnMore: 'Learn more about this scan',
      domains: 'Assets scanned',
      associatedControls: 'This scan relates to control: | This scan relates to controls:',
      associatedScans: 'This control relates to scan: | This control relates to scans:',
      scanType: 'Scan type',
      openPorts: 'Open ports',
      unableScanPorts: 'Unable to scan ports',
      riskIndicatorTooltip: {
        findings: '{count} {severity} finding | {count} {severity} findings',
        assets: '{assets} asset | {assets} assets',
        tooltip: '{findings} on {assets}',
      },
      scanRiskIndicatorTooltip:
        '{count} {severity} finding found | {count} {severity} findings found',
      scanTypes: {
        SPF: {
          title: 'SPF records configuration',
          description:
            'SPF (Sender Policy Framework) helps prevent email spoofing by specifying which servers can send email from your domain. Your SPF records need to list all legitimate email sending services while blocking unauthorised senders.',
          infractions: spfInfractions,
          displayName: 'SPF',
        },
        DKIM: {
          title: 'DKIM configuration',
          description:
            'We monitor your DKIM (DomainKeys Identified Mail) configuration to verify your emails are digitally signed and protected against tampering.',
          infractions: dkimInfractions,
          displayName: 'DKIM',
        },
        DMARC: {
          title: 'DMARC enforcement',
          description:
            'We monitor your DMARC (Domain-based Message Authentication, Reporting, and Conformance) configuration to ensure you have a strong policy that tells receiving servers how to handle suspicious emails from your domain.',
          infractions: dmarcInfractions,
          displayName: 'DMARC',
        },
        HSTS: {
          title: 'HSTS configuration',
          description:
            'We monitor your HSTS (HTTP Strict Transport Security) configuration to ensure browsers only connect to your domain using secure HTTPS connections.',
          infractions: hstsInfractions,
          displayName: 'HSTS',
        },
        CSP: {
          title: 'Content Security Policy (CSP) configuration',
          description:
            'We monitor your CSP (Content Security Policy) configuration to ensure your website only loads resources from trusted sources and prevents code injection attacks.',
          infractions: cspInfractions,
          displayName: 'CSP',
        },
        TLS: {
          title: 'TLS configuration',
          description:
            "Transport Layer Security (TLS) creates an encrypted connection between your website and its visitors. We monitor your TLS configuration to ensure it's properly securing data in transit.",
          infractions: tlsInfractions,
          displayName: 'TLS',
        },
        SecurityHeaders: {
          title: 'HTTP security headers configuration',
          description:
            "HTTP security headers tell browsers how to handle your website's content securely. We monitor these headers to ensure they're properly configured to prevent common web attacks.",
          infractions: httpInfractions,
          displayName: 'Security Headers',
        },
        DNS: {
          title: 'DNS configuration',
          description:
            'We monitor your DNS (Domain Name System) configuration to ensure your domain is correctly resolving to the right IP address and is protected against DNS spoofing attacks.',
          infractions: dnssecInfractions,
          displayName: 'DNS',
        },
        RemoteAccessPorts: {
          title: 'Remote access',
          standaloneTitle: 'Remote access ports',
          description:
            'Remote access ports are checked to identify potentially insecure services that could be used to gain entry to your network. We check for services such as SSH (port 22), RDP (port 3389), and VNC (port 5900).',
          infractions: {
            ...remoteAccessPortInfractions,
            ...portServiceVulnerableI18n,
          },
          displayName: 'Remote Access Ports',
        },
        DatabasePorts: {
          title: 'Database',
          standaloneTitle: 'Database ports',
          description:
            'Database ports are checked to ensure they are not unnecessarily exposed to the internet, which could risk a data breach. We check for services such as Microsoft SQL Server (port 1433), PostgreSQL (port 5432), and Redis (port 6379).',
          infractions: {
            ...databasePortInfractions,
            ...portServiceVulnerableI18n,
          },
          displayName: 'Database Ports',
        },
        WebServicePorts: {
          title: 'Web service',
          standaloneTitle: 'Web service ports',
          description:
            'Web service ports are checked to confirm which services are running and to identify any using unencrypted connections. We check for services such as HTTP (port 80) and HTTPS (port 443).',
          infractions: {
            ...webServicePortInfractions,
            ...portServiceVulnerableI18n,
          },
          displayName: 'Web Service Ports',
        },
        FileSharingPorts: {
          title: 'File sharing',
          standaloneTitle: 'File sharing ports',
          description:
            'File sharing ports are checked to prevent unauthorised access or the insecure transfer of data from your network. We check for services such as SMB (port 445), NFS (port 2049), and AFP (port 548).',
          infractions: {
            ...fileSharingPortInfractions,
            ...portServiceVulnerableI18n,
          },
          displayName: 'File Sharing Ports',
        },
        EmailPorts: {
          title: 'Email',
          standaloneTitle: 'Email ports',
          description:
            'Email ports are checked to detect unencrypted services that could expose credentials and email content. We check for services such as SMTP (port 25), POP3 (port 110), and IMAP (port 143).',
          infractions: {
            ...emailPortInfractions,
            ...portServiceVulnerableI18n,
          },
          displayName: 'Email Ports',
        },
        InfrastructurePorts: {
          title: 'Infrastructure',
          standaloneTitle: 'Infrastructure ports',
          description:
            'Infrastructure ports are checked for essential network services that, if misconfigured, could be exploited to disrupt or redirect your traffic. We check for services such as DNS (port 53), Kerberos (port 88), and LDAP (port 389).',
          infractions: {
            ...infrastructurePortInfractions,
            ...portServiceVulnerableI18n,
          },
          displayName: 'Infrastructure Ports',
        },
      },
      error: 'Scan unsuccessful',
      unknownAsset: 'Unknown asset',
      noIssues: 'No issues found',
      tabs: {
        overview: 'Overview',
        notes: 'Private notes',
        discussions: 'Discussions',
      },
      scanScheduled: 'Asset scan scheduled',
      scan: 'Scan: ',
      expandScan: 'Expand scan information',
      findingsCount: '{n} finding | {n} findings',
      openPortCount: '{n} open port | {n} open ports',
      noFindings: 'We scanned this asset and found no issues',
      lastScanned: 'Last scanned',
      scanInProgress: 'Scan in progress:',
      scanInProgressText: 'this may take a few minutes',
      rescan: 'Scan now',
      issues: 'Issues',
      information: 'Information',
      allFindings: 'All Findings',
      criticality: 'Criticality',
      hasRisks: 'Has risks',
      hasDiscussions: 'Has discussions',
      hasNotes: 'Has notes',
      basicConnection: "You don't have access to this asset",
      allScansUnsuccessful: 'All scans were unsuccessful',
      port: 'Port',
      ip: 'IP',
      waf: 'WAF',
      whyRisk: 'Why is this a risk?',
      cve: 'CVE',
      hasKev: 'This CVE is listed in the CISA Known Exploited Vulnerabilities (KEV) catalog.',
      hasHighEpss: 'EPSS score: {epss} — high likelihood of exploitation in the wild.',
      unknownServiceVersion: 'unknown',
      findingDetails: 'Details',
      showEmptyScansToggle: {
        show: 'Show assets without findings',
        hide: 'Hide assets without findings',
      },
    },
    contextNote: {
      emptyTitle: 'Give context to your clients on these findings',
      emptyCTA: 'Add a note',
      emptyCTAAlt: 'Add finding notes',
      CTA: 'Edit finding notes',
      note: 'Note',
      description: "Add context for your clients about this scan's findings",
      context: "Add context for your clients about this scan's findings",
      remove: 'Remove note',
    },
    onboardingModal: {
      title: 'External monitoring',
      subHeading: 'Monitor your public domains for free',
      introduction:
        'Risk Ledger now offers monitoring of your public domains, so you and select clients can have a more holistic view of your security posture.',
      setup: "You'll be able to:",
      bullet1:
        'Get visibility on security issues you might not know about and notifications when issues arise',
      bullet2: 'Understand how your domains look to both clients and attackers',
      conclusion: 'This service is {free} and you are in control of the domains that are scanned.',
      free: 'free',
      closeButton: 'See my scans',
    },
    addDomainBanner: {
      title: 'Add your assets',
      desc: "We've already scanned your registered website. Add any other assets your clients engage with, or that you want to assess for security.",
      button: 'Add another asset',
    },
    infoFindings: {
      ...infoFindingsI18n.en,
    },
    snippets: {
      copy: 'Copy to clipboard',
      copied: 'Copied',
      expand: 'Full snippet',
    },
    severityDescription: {
      CRITICAL:
        'Absence of fundamental security controls or critical misconfigurations that create immediate, exploitable vulnerabilities. Direct impact on system security and data protection. No mitigating controls in place.',
      HIGH: 'Major security control gaps or misconfigurations that significantly weaken system security. Could lead to system compromise with minimal additional conditions. Partial mitigating controls may exist.',
      MEDIUM:
        'Security controls present but suboptimal. Vulnerabilities require specific conditions to exploit. Limited impact or requires elevated privileges. Basic security measures exist but need improvement.',
      LOW: 'Minor security improvements needed. Core security intact but not following all best practices. Minimal real-world impact. Optimization opportunities rather than direct vulnerabilities.',
      INFO: 'A security indicator that provides useful context or highlights a potential error. Unlike an infraction, an informational finding does not represent a breach of best security practice, but it may offer insights or suggest areas for review.',
    },
    findings: {
      title: 'Findings',
      description: 'View all security findings from the latest scan results',
      supplierDescription:
        'Every issue our external scans found for this supplier, with the affected asset, category and severity.',
      entityName: 'finding | findings',
      columns: {
        finding: 'Finding',
        assetValue: 'Asset',
        category: 'Category',
        severityLevel: 'Severity',
        scanType: 'Scan Type',
        scannedAt: 'Scanned At',
      },
      filters: {
        filterResultsFindings: '{count} findings | {count} finding | {count} findings',
        filterResultsOutOf: '{0} out of {1}',
        assetValue: 'Asset',
        severityLevel: 'Severity Level',
        code: 'Code',
        scanType: 'Scan Type',
        noSeverity: 'No Severity',
        scanCategory: 'Scan Category',
        assetCategory: 'Asset Category',
        verificationStatus: 'Verification status',
      },
      typeLabels: {
        INFRACTION: 'Infraction',
        INFORMATIONAL: 'Informational',
      },
      placeholders: {
        errorTitle: 'Unable to load findings',
        errorDescription: 'There was an error loading the findings data. Please try again.',
        emptyTitle: 'No findings found',
        emptyDescription: 'No security findings match your current search criteria.',
      },
      verified: 'Verified',
      unverified: 'Unverified',
    },
  },
};
